/**
 * DSCR calculator workbook — `/pricing/dscr_calc*`
 * (frontend_documentation/dscr-calculator-workbook.md).
 *
 * The UW workflow BETWEEN the sizer's stated inputs and the lock snapshot: each
 * DSCR input is an evidenced row (document ids + UW haircut + verified flag),
 * and the deal's DSCR is reported at stated / validated / verified confidence.
 * Mirrors the liquidity calculator 1:1 (`closing.py::LiquiditySource`).
 *
 * ON THE PRICING ROUTER, not a router of its own — the calc feeds
 * `DSCRLock.calculated_dscr_at_lock`, so it lives with the pricing code. Save is
 * a **POST**, per the app convention the liquidity doc spells out ("don't invent
 * PUT for source rows").
 *
 * EVERY NUMBER IS BACKEND-OWNED. `statedDscr` / `validatedDscr` / `verifiedDscr`,
 * `monthlyPiPayment`, `pitiaValidated`, `componentSummary`, `clearsFloor` and
 * `fullyVerified` are computed fields on `DscrCalc`; per row, `monthlyAmount`
 * (Annual* ÷ 12), `validatedMonthlyAmount` (after the haircut) and `isIncome`
 * are computed on `DscrValueSource`. The FE renders them — it does not
 * recompute, so the tab, the quick price and the lock can't disagree.
 */
import { losHttp } from "../lib/losHttp";

const PREFIX = "/pricing";

export const dscrCalcApi = {
  /**
   * GET /pricing/dscr_calc/{dealId} → the deal's DscrCalc.
   * When nothing is stored yet the server SEEDS the rows from the units and
   * subject property (prepopulation), so a first visit is already populated —
   * UW reviews and verifies rather than keying blanks.
   */
  get: (dealId) =>
    losHttp.get(`${PREFIX}/dscr_calc/${dealId}`).then((r) => r.data),

  /**
   * POST /pricing/dscr_calc/{dealId} — body `{ sources[], programFloor }`; the
   * rest of the document is derived. Returns the recomputed calc.
   *
   * Side effects worth knowing: the save writes `verifiedDscr → deal.dscr` and
   * the rent rollup → `totalInPlaceRents`, so the loan record moves too. Always
   * render from the RESPONSE — a verified row whose amount changed comes back
   * `verified=false` (enforced server-side since 2026-08-25).
   *
   * Since 2026-08-25 the document also carries its OWN audit trail: each save
   * with changes appends `changeHistory[]` `{changedBy, changedDateTime,
   * changes: {rows_added[], rows_removed[], rows_changed[{row, fields:
   * {field: {old, new}}}], programFloor: {old, new}}}` (no-change saves append
   * nothing) and lands a `dscr_calc_save` event in the loan Activity Log. The
   * FIRST save with sources captures `initialEntry` `{dscr, totalInPlaceRents,
   * totalMarketRents, capturedAt}` — the loan's pre-workbook values, kept so
   * "initial entry vs finalized" stays visible after the writeback replaces
   * them. Both come back on the GET; the tab's change-history drawer renders
   * them.
   */
  save: (dealId, { sources, programFloor }) =>
    losHttp
      .post(`${PREFIX}/dscr_calc/${dealId}`, { sources, programFloor })
      .then((r) => r.data),

  /**
   * GET /pricing/dscr_calc_components → the Add-Source catalog:
   * `[{ componentType, bucket: "income"|"expense", annual: bool }]`.
   * Fetched rather than hardcoded so a new component needs no FE release;
   * DSCR_COMPONENTS in the tab's constants is the offline fallback.
   */
  components: () =>
    losHttp.get(`${PREFIX}/dscr_calc_components`).then((r) => {
      const data = r.data;
      return Array.isArray(data) ? data : data?.components || [];
    }),

  /**
   * GET /pricing/dscr_calc_evidence/{dealId} → `{ dealId, evidence[] }` (added
   * 2026-09-15, `services/dscr_evidence.py`): one entry per loan document
   * that has a DSCR-relevant structured extract, projected by component so the
   * Add-Source drawers can show "the document says …" + Use-this-value without
   * knowing the extract schemas.
   *
   *   { loanDocumentId, blobName, docType, docGroup, fileName, documentStatus,
   *     extractorName, extractVersion, extractedAt,
   *     componentTypes: ["Gross Monthly Rent"],   // which drawer(s) it evidences
   *     rentBasis: "in_place"|"market"|null,       // lease → in_place, appraisal → market
   *     unitDesignator: "2"|null,                  // lease property_address.unit
   *     candidates: { monthlyRent: {value, path}, leaseStartDate, …
   *                   | marketRentMonthly, asIsValue
   *                   | annualPremium, effectiveDate, expirationDate, dwellingCoverage, floodCoverage
   *                   | annualTaxes, taxYear },
   *     flags: {…}, detail: {…} }
   *
   * DISPLAY ONLY — nothing here writes units or the deal; "Use this value" is a
   * client-side fill of the row's `amount`, and the appraisal's per-unit-TYPE
   * `detail.unitMix[]` is for the UW to assign to a unit by hand.
   */
  evidence: (dealId) =>
    losHttp
      .get(`${PREFIX}/dscr_calc_evidence/${dealId}`)
      .then((r) => (Array.isArray(r.data?.evidence) ? r.data.evidence : [])),
};

export default dscrCalcApi;
